
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface TeacherProfile {
  teacher_code: string;
  full_name: string;
  nickname: string;
  phone_number: string;
  teaching_experience: string;
  academic_achievements: string;
  status: string;
  subjects: any[];
  specialized: Array<{
    name: string;
    description?: string | null;
  }>;
  availability_templates: Array<{
    day_of_week: number;
    start_time: string;
    end_time: string;
    slot_order: number;
  }>;
}

export const useTeacherProfiles = (includeInactive: boolean = false) => {
  return useQuery({
    queryKey: ['teacher-profiles', includeInactive ? 'all' : 'active'],
    queryFn: async () => {
      let query = supabase
        .from('teacher_profiles_view')
        .select('*', { count: 'exact' })
        .order('teacher_code', { ascending: true });

      // Only filter by ACTIVE status if includeInactive is false
      if (!includeInactive) {
        query = query.eq('status', 'ACTIVE');
      }

      const { data, error, count } = await query;

      if (error) {
        console.error('Error fetching teacher profiles:', error);
        throw error;
      }

      // Transform the data to match our interface
      const transformedData: TeacherProfile[] = (data || []).map(profile => ({
        teacher_code: profile.teacher_code || '',
        full_name: profile.full_name || '',
        nickname: profile.nickname || '',
        phone_number: profile.phone_number || '',
        teaching_experience: profile.teaching_experience || '',
        academic_achievements: profile.academic_achievements || '',
        status: profile.status || '',
        subjects: Array.isArray(profile.subjects)
          ? profile.subjects
          : [],
        specialized: Array.isArray(profile.specialized)
          ? profile.specialized.map((spec: any) => ({
              name: spec?.name || '',
              description: spec?.description || null
            }))
          : [],
        availability_templates: Array.isArray(profile.availability_templates)
          ? profile.availability_templates.map((template: any) => ({
              day_of_week: template?.day_of_week || 0,
              start_time: template?.start_time || '',
              end_time: template?.end_time || '',
              slot_order: template?.slot_order || 99
            }))
          : []
      }));

      return {
        data: transformedData,
        totalCount: count || 0,
        totalPages: Math.ceil((count || 0) / 20) // For reference, though not used in client-side filtering
      };
    },
  });
};
